/**
 * @fileoverview E&O Policy Management Store
 *
 * MobX store for managing Errors & Omissions (E&O) insurance policies during agent onboarding.
 * Handles policy data fetching, validation, PDF uploads, and policy type selection (personal vs company).
 *
 * Key Features:
 * - Dual policy support (personal and company policies)
 * - Debounced auto-save for policy updates
 * - PDF certificate upload with base64 encoding
 * - Policy validation with date logic checking
 * - Integration with user_metas for policy type persistence
 * - System settings integration for group policy URLs
 *
 * Related Documentation:
 * - /docs/modules/AgentEandOManager/store.md - Complete store reference
 * - /docs/modules/AgentEandOManager/business-logic.md - Validation rules
 * - /docs/modules/AgentEandOManager/database.md - Database schema
 *
 * @module components/agents/AgentEandOManager/Eopolicy.store
 * @requires mobx
 * @requires moment
 */

import { makeAutoObservable, runInAction } from 'mobx'
import SystemSettingService from './../../../shared/services/SystemSetting.service'
import UserProfileService from './../../../shared/services/UserProfile.service'
import UserService from './../../../shared/services/User.service'
import UserEopolicyService from './../../../shared/services/UserEopolicy.service'
import { UserEopolicyFactory } from './../../../shared/factories'
import { b64toBlob } from './../../../shared/utilities/b64toBlob.function'
import moment from 'moment'

/**
 * Cache for group E&O policy URL from system_settings
 * Prevents repeated API calls to fetch vendors---eo-group-policy setting
 *
 * @private
 * @type {{loaded: boolean, url: string|null}}
 */
const GROUP_POLICY = {
  loaded: false,
  url: null,
}

/**
 * Serialize date value to YYYY-MM-DD format
 *
 * Handles multiple input formats:
 * - ISO 8601 timestamp strings (2024-12-21T00:00:00.000Z)
 * - Date objects
 * - Already formatted YYYY-MM-DD strings
 *
 * @private
 * @param {string|Date|null} dateVal - Date value to serialize
 * @returns {string|null} Date in YYYY-MM-DD format or original value if invalid
 *
 * @example
 * serializeDate('2024-12-21T00:00:00.000Z') // '2024-12-21'
 * serializeDate(new Date(2024, 11, 21))     // '2024-12-21'
 * serializeDate('2024-12-21')               // '2024-12-21'
 */
const serializeDate = dateVal => {
  if (dateVal && typeof dateVal === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(dateVal)) {
      dateVal = moment(dateVal, 'YYYY-MM-DDThh:mm:ss.SSSZ').format('YYYY-MM-DD')
    }
  }

  if (dateVal && typeof dateVal === 'object') {
    dateVal = moment(dateVal).utc().format('YYYY-MM-DD')
  }

  return dateVal
}

/**
 * Validate that effective_date is before expiration_date
 *
 * Business Rule: E&O policies must have effective date earlier than expiration date
 * to be considered valid. This prevents logical errors like policies that expire
 * before they begin.
 *
 * @private
 * @param {object} Policy - UserEopolicy model instance
 * @returns {boolean} True if dates are valid (effective < expiration) or if dates not set
 *
 * @example
 * // Valid: effective before expiration
 * policy.set('effective_date', '2024-01-01')
 * policy.set('expiration_date', '2025-01-01')
 * getIsDatesValid(policy) // true
 *
 * // Invalid: effective after expiration
 * policy.set('effective_date', '2025-01-01')
 * policy.set('expiration_date', '2024-01-01')
 * getIsDatesValid(policy) // false
 *
 * // Valid: dates not set yet
 * policy.set('effective_date', null)
 * getIsDatesValid(policy) // true (allows incomplete policies)
 */
const getIsDatesValid = Policy => {
  let effective_date = Policy?.get('effective_date'),
    expiration_date = Policy?.get('expiration_date')

  effective_date =
    effective_date && typeof effective_date === 'string'
      ? moment(`${effective_date}`.split('T').shift()).toDate()
      : effective_date
  expiration_date =
    expiration_date && typeof expiration_date === 'string'
      ? moment(`${expiration_date}`.split('T').shift()).toDate()
      : expiration_date

  if (effective_date && expiration_date) {
    if (typeof effective_date === 'object' && typeof expiration_date === 'object') {
      if (effective_date.getTime() >= expiration_date.getTime()) return false
    }
  }

  return true
}

/**
 * Validate that policy has all required fields populated
 *
 * Validates 7 required fields for personal E&O policies:
 * 1. carrier_name - Insurance carrier name
 * 2. eopolicy_num - Policy number from carrier
 * 3. per_coverage_amt - Per occurrence coverage amount
 * 4. agg_coverage_amt - Aggregate coverage amount
 * 5. effective_date - Policy effective date
 * 6. expiration_date - Policy expiration date
 * 7. eopolicy_pdf - Certificate PDF URL
 *
 * Business Rules:
 * - All 7 fields required for personal policies
 * - Company policies exempt from PDF requirement (handled by skipProps)
 * - Date logic validated separately (effective < expiration)
 * - Coverage amounts must be > 0 (not enforced here, UI validation)
 *
 * @private
 * @param {object} Policy - UserEopolicy model instance
 * @param {string[]} [skipProps=[]] - Array of property names to skip in validation
 * @returns {boolean} True if all required fields present and dates valid
 *
 * @example
 * // Valid personal policy
 * getIsValid(personalPolicy) // true if all 7 fields set
 *
 * // Company policy (skip PDF requirement)
 * getIsValid(companyPolicy, ['eopolicy_pdf']) // true if 6 fields set
 *
 * // Invalid: missing carrier_name
 * policy.set('carrier_name', null)
 * getIsValid(policy) // false
 */
function getIsValid(Policy, skipProps) {
  skipProps = Array.isArray(skipProps) ? skipProps : []

  // console.log("getIsValid > ",{
  //   // id: Policy?.id(),
  //   carrier_name: (Policy?.get('carrier_name') || skipProps.includes('carrier_name')),
  //   per_coverage_amt: (Policy?.get('per_coverage_amt') || skipProps.includes('per_coverage_amt')),
  //   agg_coverage_amt: (Policy?.get('agg_coverage_amt') || skipProps.includes('agg_coverage_amt')),
  //   eopolicy_num: (Policy?.get('eopolicy_num') || skipProps.includes('eopolicy_num')),
  //   eopolicy_pdf: (Policy?.get('eopolicy_pdf') || skipProps.includes('eopolicy_pdf')),
  //   effective_date: (Policy?.get('effective_date') || skipProps.includes('effective_date')),
  //   expiration_date: (Policy?.get('expiration_date') || skipProps.includes('expiration_date')),
  //   skipProps
  // });

  // Only use the provided eo, do NOT close over eopolicy state!
  const isValid = !!(
    // Policy?.id() &&
    (
      (Policy?.get('carrier_name') || skipProps.includes('carrier_name')) &&
      (Policy?.get('per_coverage_amt') || skipProps.includes('per_coverage_amt')) &&
      (Policy?.get('agg_coverage_amt') || skipProps.includes('agg_coverage_amt')) &&
      (Policy?.get('eopolicy_num') || skipProps.includes('eopolicy_num')) &&
      (Policy?.get('eopolicy_pdf') || skipProps.includes('eopolicy_pdf')) &&
      (Policy?.get('effective_date') || skipProps.includes('effective_date')) &&
      (Policy?.get('expiration_date') || skipProps.includes('expiration_date'))
    )
  )

  if (isValid) return getIsDatesValid(Policy)

  return false
}

/**
 * Fetch group E&O policy PDF URL from system_settings
 *
 * Retrieves the 'vendors---eo-group-policy' system setting which contains
 * the URL to the company-supplied E&O certificate PDF. This URL is used
 * for display/reference purposes (not for validation).
 *
 * Caches result in GROUP_POLICY object to prevent repeated API calls.
 *
 * @private
 * @async
 * @returns {Promise<void>} Updates GROUP_POLICY.url and GROUP_POLICY.loaded
 *
 * @example
 * await fetchGroupEopolicy()
 * // GROUP_POLICY.url = 'https://storage.googleapis.com/.../group-policy.pdf'
 * // GROUP_POLICY.loaded = true
 */
const fetchGroupEopolicy = async () => {
  if (GROUP_POLICY.loaded !== true) {
    try {
      const Policy = (
        (
          await SystemSettingService.search({
            search: { setting_key: 'vendors---eo-group-policy' },
          })
        )?.models || []
      ).shift()
      if (Policy && Policy.setting_value) GROUP_POLICY.url = JSON.parse(Policy.setting_value)?.url
      GROUP_POLICY.loaded = true
    } catch (ex) {
      GROUP_POLICY.loaded = true
    }
  }
}

/**
 * Fetch single E&O policy by ID and deserialize dates
 *
 * Retrieves a specific policy record and converts date strings from database
 * format (YYYY-MM-DDTHH:MM:SS) to Date objects for use in date picker components.
 *
 * Used after PDF upload to refresh policy with updated eopolicy_pdf URL.
 *
 * @private
 * @async
 * @param {number} policyId - Policy record ID from eopolicies table
 * @returns {Promise<object|null>} UserEopolicy model instance or null if not found
 *
 * @example
 * const policy = await fetchPolicy(123)
 * policy.get('effective_date') // Date object, not string
 * policy.get('eopolicy_pdf') // Updated URL after upload
 */
const fetchPolicy = async policyId => {
  let eopolicy = null

  try {
    eopolicy = await UserEopolicyFactory.findById(policyId)
  } catch (ex) {}

  if (eopolicy?.get('effective_date'))
    eopolicy.set('effective_date', moment(eopolicy.get('effective_date').split('T').shift()).toDate())
  if (eopolicy?.get('expiration_date'))
    eopolicy.set('expiration_date', moment(eopolicy.get('expiration_date').split('T').shift()).toDate())

  return eopolicy
}

/**
 * Fetch or create both personal and company E&O policies for user
 *
 * Ensures every user has exactly 2 policy records:
 * - One with eopolicy_type='personal'
 * - One with eopolicy_type='company'
 *
 * Creates missing records automatically using UserEopolicyFactory.create().
 * Converts date fields from ISO strings to Date objects for UI components.
 * Triggers group policy URL fetch in background (non-blocking).
 *
 * Database Constraint: UNIQUE KEY idx_user_policy_type (user_id, eopolicy_type)
 * ensures max 1 policy of each type per user.
 *
 * @private
 * @async
 * @param {number} userId - User ID from users table
 * @returns {Promise<{personal: object, company: object}>} Both policy instances
 *
 * @example
 * const policies = await fetchPolicies(15560)
 * policies.personal.get('eopolicy_type') // 'personal'
 * policies.company.get('eopolicy_type')  // 'company'
 */
const fetchPolicies = async userId => {
  let eopolicies = { personal: null, company: null }

  try {
    const policies = await UserEopolicyFactory.findAllByUserId(userId)
    policies.forEach(policy => {
      if (eopolicies.hasOwnProperty(policy.get('eopolicy_type'))) eopolicies[policy.get('eopolicy_type')] = policy
    })

    if (!eopolicies.personal)
      eopolicies.personal = await UserEopolicyFactory.create({
        user_id: userId,
        eopolicy_type: 'personal',
      })

    if (!eopolicies.company)
      eopolicies.company = await UserEopolicyFactory.create({
        user_id: userId,
        eopolicy_type: 'company',
      })
  } catch (ex) {}

  for (var e in eopolicies) {
    if (eopolicies[e]?.get('effective_date'))
      eopolicies[e].set('effective_date', moment(eopolicies[e].get('effective_date').split('T').shift()).toDate())
    if (eopolicies[e]?.get('expiration_date'))
      eopolicies[e].set('expiration_date', moment(eopolicies[e].get('expiration_date').split('T').shift()).toDate())
  }

  if (GROUP_POLICY.loaded !== true) fetchGroupEopolicy()

  return eopolicies
}

/**
 * Find or create user_metas record for policy type selection
 *
 * Retrieves or creates a user_metas record with:
 * - meta_key: 'profile---eo-policy-type'
 * - meta_value: 'personal' | 'company' | null
 *
 * This record persists the agent's policy type choice across sessions.
 * Used during onboarding to remember which E&O option agent selected.
 *
 * Database Table: user_metas
 * Constraint: UNIQUE KEY idx_user_meta_key (user_id, meta_key)
 *
 * @private
 * @async
 * @param {object} User - User model instance
 * @returns {Promise<object>} UserMeta model instance
 *
 * @example
 * const meta = await findOrCreateMeta(User)
 * meta.get('meta_key')   // 'profile---eo-policy-type'
 * meta.get('meta_value') // 'personal' or 'company' or null
 */
const findOrCreateMeta = async User => {
  let Meta = ((await User.meta(true).key('profile---eo-policy-type')) || []).shift()

  if (!Meta) {
    Meta = await User.meta(true).create({
      meta_key: 'profile---eo-policy-type',
    })
    await Meta.save()
  }

  return Meta
}

/**
 * MobX Store for E&O Policy Management
 *
 * Central state management for E&O insurance policies during agent onboarding.
 * Handles data fetching, validation, PDF uploads, and policy type selection.
 *
 * Key Responsibilities:
 * - Fetch/create dual policies (personal + company) for each user
 * - Persist policy type selection in user_metas
 * - Debounced auto-save for policy field updates
 * - PDF upload with base64 encoding and server submission
 * - Validation logic for personal policies (7 required fields)
 * - Date deserialization for UI components
 *
 * Business Rules Implemented:
 * - Personal policies require all 7 fields + PDF
 * - Company policies auto-valid (fields optional, PDF uploaded by staff)
 * - Effective date must be < expiration date
 * - Each user has max 1 personal and 1 company policy (DB constraint)
 *
 * @class
 * @property {boolean} isLoading - Loading state during initialization
 * @property {boolean} isSaving - Save in progress flag
 * @property {boolean} isDeleting - Delete in progress flag
 * @property {boolean} isUploading - PDF upload in progress flag
 * @property {object|null} User - Current User model instance
 * @property {object|null} Policy - Active policy (personal or company based on policyType)
 * @property {{personal: object|null, company: object|null}} Policies - Both policy instances
 * @property {object} showValidity - Tracks which fields to show validation errors for
 * @property {boolean} showModal - Modal visibility flag
 * @property {number|null} dbTimer - Debounce timer ID for auto-save
 * @property {string|null} policyType - Current selected type: 'personal' | 'company' | null
 * @property {object|null} UserPolicyMeta - user_metas record storing policy type selection
 *
 * @example
 * import EopolicyStore from './Eopolicy.store'
 *
 * // Initialize for user
 * await EopolicyStore.initUser(User)
 *
 * // Select policy type
 * EopolicyStore.setPolicyType('personal')
 *
 * // Update field (auto-saves after 500ms)
 * EopolicyStore.Policy.set('carrier_name', 'State Farm')
 * await EopolicyStore.savePolicy({ debounce: 500 })
 *
 * // Upload PDF
 * await EopolicyStore.onSelectFile(fileObject)
 *
 * // Validate
 * const isValid = EopolicyStore.getIsValid()
 */
class EopolicyStore {
  /** @type {boolean} Loading state during initialization */
  isLoading = false

  /** @type {boolean} Save in progress flag */
  isSaving = false

  /** @type {boolean} Delete in progress flag */
  isDeleting = false

  /** @type {boolean} PDF upload in progress flag */
  isUploading = false

  /** @type {object|null} Current User model instance */
  User = null

  /** @type {object|null} Active policy (based on policyType) */
  Policy = null

  /** @type {{personal: object|null, company: object|null}} Both policy instances */
  Policies = { personal: null, company: null }

  /** @type {object} Tracks which fields show validation errors */
  showValidity = {}

  /** @type {boolean} Modal visibility flag */
  showModal = false

  /** @type {number|null} Debounce timer ID for auto-save */
  dbTimer = null

  /** @type {string|null} Current selected type: 'personal' | 'company' | null */
  policyType = null

  /** @type {object|null} user_metas record for policy type selection */
  UserPolicyMeta = null

  /** @type {object|null} Cached company E&O template from system_settings */
  companyEOTemplate = null

  /**
   * Creates EopolicyStore instance with MobX observables
   * @constructor
   */
  constructor() {
    makeAutoObservable(this)
  }

  /**
   * Set policy type and persist to user_metas
   *
   * Updates policyType observable and saves selection to user_metas table.
   * This persists the agent's choice across sessions.
   *
   * NEW: Auto-populates company policy fields when 'company' type selected.
   *
   * Triggers component re-renders via MobX reactivity:
   * - EandOPolicySupplier updates button selection
   * - EandOForm switches between personal/company policy fields
   * - EandOPolicyUploader updates read-only state
   *
   * @param {string} type - 'personal' or 'company'
   * @returns {void}
   *
   * @example
   * // Agent clicks "Personal" button
   * Store.setPolicyType('personal')
   * // policyType = 'personal'
   * // UserPolicyMeta.meta_value = 'personal' (saved to database)
   * // Components re-render with personal policy data
   *
   * @example
   * // Agent clicks "USABG" button
   * Store.setPolicyType('company')
   * // policyType = 'company'
   * // applyCompanyTemplate() called → fields auto-populate
   * // UserPolicyMeta.meta_value = 'company' (saved to database)
   */
  setPolicyType = type => {
    this.policyType = type
    if (type) {
      if (this.UserPolicyMeta && this.UserPolicyMeta.get('meta_value') !== type) {
        this.UserPolicyMeta.set('meta_value', type)
        this.UserPolicyMeta.save()
      }

      // Auto-populate company policy fields when company type selected
      if (type === 'company') {
        this.applyCompanyTemplate()
      }
    }
  }

  /**
   * Get group E&O policy PDF URL
   *
   * Returns cached URL from vendors---eo-group-policy system setting.
   * Used for displaying company-supplied certificate to agents.
   *
   * @returns {string|false} PDF URL or false if not loaded
   *
   * @example
   * const url = Store.getGroupUrl()
   * // 'https://storage.googleapis.com/.../group-policy.pdf' or false
   */
  getGroupUrl = () => GROUP_POLICY.loaded && GROUP_POLICY.url

  /**
   * Initialize store for user (onboarding entry point)
   *
   * Main initialization method called when AgentEandOManager mounts.
   * Performs complete setup in parallel:
   * 1. Fetch or create both policy records (personal + company)
   * 2. Fetch or create policy type meta record
   * 3. Set active policy based on saved selection
   * 4. Initialize validity tracking for fields
   *
   * Idempotent: Safe to call multiple times for same user (returns false if already initialized).
   *
   * Data Flow:
   * - Calls fetchPolicies() to get/create policy records
   * - Calls findOrCreateMeta() to get policy type selection
   * - Calls fetchCompanyEOTemplate() to pre-load company E&O template (NEW)
   * - Sets this.Policy to active policy (based on policyType)
   * - Initializes showValidity object for validation UI
   *
   * @async
   * @param {object} User - User model instance
   * @param {string} [eopolicyType] - Optional policy type (unused parameter, kept for compatibility)
   * @returns {Promise<boolean>} True if initialized, false if already initialized for this user
   *
   * @example
   * // In AgentEandOManager.componentDidMount()
   * const initialized = await Store.initUser(User)
   * if (initialized) {
   *   console.log('Store initialized')
   *   console.log('Policy type:', Store.policyType) // 'personal' or 'company' or null
   *   console.log('Template pre-loaded:', !!Store.companyEOTemplate) // true
   * }
   */
  initUser = async (User, eopolicyType) => {
    if (this.User?.id() === User?.id()) return false

    if (!User || !User?.id()) return false

    this.isLoading = true

    this.User = User
    if (User) {
      const [Policies, UserPolicyMeta] = await Promise.all([
        fetchPolicies(this.User.id()),
        findOrCreateMeta(this.User),
        this.fetchCompanyEOTemplate(), // Pre-load template (no latency when clicking USABG)
      ])

      runInAction(() => {
        this.Policies = Policies
        this.UserPolicyMeta = UserPolicyMeta
        this.policyType = this.UserPolicyMeta && this.UserPolicyMeta?.get('meta_value')

        if (this.policyType && this.Policies[this.policyType]) {
          this.Policy = this.Policies[this.policyType]
          if (this.Policy) {
            // Set policy validity.
            ;[
              'effective_date',
              'expiration_date',
              'carrier_name',
              'per_coverage_amt',
              'agg_coverage_amt',
              'eopolicy_num',
            ].forEach(key => {
              if (this.Policy.get(key)) this.showValidity[key] = false
            })
          }
        }

        // Initialize eopolicy_pdf validity
        this.showValidity.eopolicy_pdf = !this.Policy?.get('eopolicy_pdf')

        this.isLoading = false
      })
    } else {
      this.isLoading = false
    }

    return true
  }

  /**
   * Handle PDF file selection and upload
   *
   * Complete PDF upload workflow:
   * 1. Read file as base64 using FileReader
   * 2. Extract MIME type and raw base64 data
   * 3. Convert base64 to Blob
   * 4. Build FormData with file and metadata
   * 5. Upload to server via UserService.uploadEopolicy()
   * 6. Refresh policy to get updated eopolicy_pdf URL
   * 7. Update this.Policy with refreshed data
   *
   * File Requirements:
   * - Policy must be saved first (must have ID)
   * - Max size: 10MB (enforced by component)
   * - Allowed types: PDF, JPEG, PNG (enforced by component)
   *
   * Upload Endpoint: POST /api/users/:userId/eopolicy
   * FormData Fields:
   * - assumed_user_id: Current user ID
   * - eopolicy_pdf: File blob
   * - eopolicy_pdf_filename: Original filename
   * - eopolicy_pdf_id: Policy record ID
   *
   * @async
   * @param {File} file - File object from input[type="file"]
   * @returns {Promise<void>} Resolves when upload complete
   * @throws {Error} If upload fails (logged to console)
   *
   * @example
   * // In EandOPolicyUploader
   * const handleFileSelect = async (event) => {
   *   const file = event.target.files[0]
   *   await Store.onSelectFile(file)
   *   // Store.Policy.get('eopolicy_pdf') now has URL
   *   // Store.isUploading = false
   * }
   */
  onSelectFile = async file => {
    if (!this.Policy || !this.Policy?.id()) return

    const reader = new FileReader()

    return new Promise((resolve, reject) => {
      reader.addEventListener('load', async () => {
        this.isUploading = true
        this.Policy.set('eopolicy_pdf_src', {
          filename: file?.name,
          data: reader.result,
        })

        // Extract content-type from complete base64 payload.
        let match = this.Policy.get('eopolicy_pdf_src')
          .data.split(',')[0]
          .match(/\w+\/\w+/)
        match = match && match.shift()

        // Extract raw base64 data w/out content-type.
        let base64 = this.Policy.get('eopolicy_pdf_src').data.split(',')[1]

        // Build payload to send file to the server.
        const formData = new FormData()
        formData.append('assumed_user_id', UserProfileService.getUserId(true))
        formData.append('eopolicy_pdf', b64toBlob(base64, match))
        formData.append('eopolicy_pdf_filename', file.name)
        formData.append('eopolicy_pdf_id', this.Policy.id())

        try {
          await UserService.uploadEopolicy(this.User.id(), formData)
          const type = this.Policy?.get('eopolicy_type')
          const Policies = await fetchPolicies(this.User.id())
          runInAction(() => {
            this.Policies = Policies
            if (type && Policies[type]) this.Policy = Policies[type]
          })
        } catch (ex) {
          console.error('Failed to upload: ', ex)
        }

        this.isUploading = false
        return resolve()
      })

      reader.readAsDataURL(file)
    })
  }

  /**
   * Save policy to database with optional debouncing
   *
   * Persists policy field changes to eopolicies table.
   * Supports debounced auto-save to prevent excessive database writes
   * during rapid user input (typing, dragging date pickers, etc.).
   *
   * Save Workflow:
   * 1. Validate minimum required fields (carrier_name, eopolicy_num)
   * 2. Serialize date fields to YYYY-MM-DD format
   * 3. Call Policy.save() (updates via API)
   * 4. Set isSaving flags appropriately
   *
   * Debounce Behavior:
   * - If opts.debounce > 0: Delays save by specified milliseconds
   * - If called again during delay: Cancels previous timer, starts new one
   * - Result: Only 1 save occurs per debounce period (reduces API calls)
   *
   * Early Return Conditions:
   * - carrier_name not set → no save
   * - eopolicy_num not set → no save
   * (Prevents saving incomplete policies during initial entry)
   *
   * @async
   * @param {object} [opts] - Options object
   * @param {number} [opts.debounce=0] - Debounce delay in milliseconds (0 = immediate save)
   * @returns {Promise<boolean>} True if save succeeded, false if failed
   *
   * @example
   * // Immediate save
   * await Store.savePolicy()
   *
   * // Debounced save (500ms delay)
   * await Store.savePolicy({ debounce: 500 })
   *
   * // Typical usage in field update
   * Store.Policy.set('carrier_name', 'ABC Insurance')
   * Store.savePolicy({ debounce: 500 }) // Waits 500ms before saving
   */
  savePolicy = async opts => {
    const { debounce } = (opts = opts && typeof opts === 'object' ? opts : {})

    if (!this.Policy.get('carrier_name') || !this.Policy.get('eopolicy_num')) return

    const completeSave = async () => {
      this.isSaving = true

      if (this.Policy.get('effective_date'))
        this.Policy.set('effective_date', serializeDate(this.Policy.get('effective_date')))

      if (this.Policy.get('expiration_date'))
        this.Policy.set('expiration_date', serializeDate(this.Policy.get('expiration_date')))

      try {
        await this.Policy.save()
        this.isSaving = false
        return true
      } catch (ex) {
        console.error(`${ex}`)
        this.isSaving = false
        return false
      }
    }

    if (debounce > 0) {
      if (this.dbTimer !== null) {
        window.clearTimeout(this.dbTimer)
        this.dbTimer = null
      }

      return await new Promise((resolve, reject) => {
        this.dbTimer = window.setTimeout(() => {
          completeSave()
            .then(
              res => resolve(res),
              err => reject(err)
            )
            .finally(() => {
              if (this.dbTimer !== null) {
                window.clearTimeout(this.dbTimer)
                this.dbTimer = null
              }
            })
        }, debounce)
      })
    }

    return await completeSave()
  }

  /**
   * Delete policy PDF certificate
   *
   * Removes eopolicy_pdf URL from policy record and saves to database.
   * Typically called from confirmation modal when user wants to replace/remove certificate.
   *
   * Workflow:
   * 1. Set isDeleting flag
   * 2. Clear eopolicy_pdf field (set to empty string)
   * 3. Save policy (removes PDF reference from database)
   * 4. Close modal
   * 5. Clear isDeleting flag
   *
   * Note: Does NOT delete file from storage (GCS), only removes database reference.
   * Actual file deletion handled by backend garbage collection (if implemented).
   *
   * @async
   * @returns {Promise<boolean>} Always returns true
   *
   * @example
   * // In modal confirmation
   * Store.showModal = true // Show "Delete PDF?" modal
   *
   * // User clicks "Confirm"
   * await Store.deletePolicyPdf()
   * // Policy.get('eopolicy_pdf') = ''
   * // showModal = false
   */
  deletePolicyPdf = async () => {
    this.isDeleting = true
    this.Policy.set('eopolicy_pdf', '')
    this.savePolicy()
    this.showModal = false
    this.isDeleting = false
    return true
  }

  /**
   * Validate that active policy dates are logically valid
   *
   * Wrapper around utility function getIsDatesValid(Policy).
   * Checks that effective_date is before expiration_date.
   *
   * Business Rule: E&O policies must have effective date earlier than
   * expiration date to be considered valid.
   *
   * @returns {boolean} True if dates valid (effective < expiration) or not set
   *
   * @example
   * Store.Policy.set('effective_date', '2024-01-01')
   * Store.Policy.set('expiration_date', '2025-01-01')
   * Store.getIsDatesValid() // true
   *
   * Store.Policy.set('expiration_date', '2023-01-01')
   * Store.getIsDatesValid() // false (expiration before effective)
   */
  getIsDatesValid = () => {
    return getIsDatesValid(this.Policy)
  }

  /**
   * Validate that active policy meets all requirements
   *
   * Complete validation check for policy readiness.
   * Verifies all 7 required fields are populated and date logic is valid.
   *
   * Required Fields:
   * 1. carrier_name
   * 2. eopolicy_num
   * 3. per_coverage_amt
   * 4. agg_coverage_amt
   * 5. effective_date
   * 6. expiration_date
   * 7. eopolicy_pdf
   *
   * Business Rules:
   * - Personal policies: All 7 fields required
   * - Company policies: Can skip PDF via skipProps (uploaded by staff later)
   * - Dates must be logically valid (effective < expiration)
   *
   * Used by onboarding flow to determine if agent can proceed to next stage.
   *
   * @param {object} [opts] - Options object
   * @param {string[]} [opts.skipProps] - Array of field names to skip in validation
   * @returns {boolean} True if policy meets all requirements
   *
   * @example
   * // Personal policy validation (all fields required)
   * Store.getIsValid() // true/false
   *
   * // Company policy validation (skip PDF requirement)
   * Store.getIsValid({ skipProps: ['eopolicy_pdf'] }) // true if 6 fields set
   *
   * // In onboarding flow
   * const canProceed = Store.getIsValid()
   * if (canProceed) {
   *   // Enable "Next" button
   * }
   */
  getIsValid = opts => {
    const { skipProps } = opts || {}
    return getIsValid(this.Policy, skipProps)
  }

  /**
   * Fetch company E&O template from system_settings
   *
   * Retrieves the 'agents---eo-policy' system setting which contains
   * the template for auto-populating company-supplied E&O policies.
   *
   * Template Structure (JSON):
   * {
   *   "carrier_name": "Berkshire Hathaway Direct Insurance Company",
   *   "eopolicy_num": "N9PL597538",
   *   "per_coverage_amt": "1000000",
   *   "agg_coverage_amt": "3000000",
   *   "effective_date": "2024-12-21",
   *   "expiration_date": "2025-12-21"
   * }
   *
   * Caches result in this.companyEOTemplate observable.
   * Called during initUser() to pre-load template (no latency on USABG click).
   *
   * Error Handling: Fails gracefully if setting not found (logs warning, continues).
   *
   * @async
   * @returns {Promise<void>} Updates this.companyEOTemplate
   *
   * @example
   * await Store.fetchCompanyEOTemplate()
   * // Store.companyEOTemplate = { carrier_name: "...", ... }
   */
  fetchCompanyEOTemplate = async () => {
    try {
      const settings = await SystemSettingService.search({
        search: { setting_key: 'agents---eo-policy' },
      })

      const setting = settings?.models?.[0]

      if (!setting || !setting.setting_value) {
        console.warn('[Eopolicy.store] Company E&O template not found in system_settings')
        return
      }

      // Parse JSON template (handle both string and already-parsed object)
      const template =
        typeof setting.setting_value === 'string' ? JSON.parse(setting.setting_value) : setting.setting_value

      // Validate required fields
      const requiredFields = [
        'carrier_name',
        'eopolicy_num',
        'per_coverage_amt',
        'agg_coverage_amt',
        'effective_date',
        'expiration_date',
      ]

      for (const field of requiredFields) {
        if (!template[field]) {
          console.warn(`[Eopolicy.store] Company E&O template missing field: ${field}`)
          return
        }
      }

      // Cache template
      runInAction(() => {
        this.companyEOTemplate = template
      })

      console.log('[Eopolicy.store] Company E&O template loaded successfully')
    } catch (error) {
      console.error('[Eopolicy.store] Failed to fetch company E&O template:', error)
    }
  }

  /**
   * Apply company E&O template to company policy
   *
   * Auto-populates all 6 company policy fields from cached template.
   * Called when user clicks "USABG" button (company-supplied E&O).
   *
   * Workflow:
   * 1. Check if template cached (companyEOTemplate exists)
   * 2. Apply template values to Policies.company fields
   * 3. Save updated policy to database
   * 4. Update observables (triggers UI re-render)
   *
   * Field Mapping:
   * - template.carrier_name → companyPolicy.carrier_name
   * - template.eopolicy_num → companyPolicy.eopolicy_num
   * - template.per_coverage_amt → companyPolicy.per_coverage_amt (parsed to float)
   * - template.agg_coverage_amt → companyPolicy.agg_coverage_amt (parsed to float)
   * - template.effective_date → companyPolicy.effective_date (converted to Date)
   * - template.expiration_date → companyPolicy.expiration_date (converted to Date)
   *
   * Note: PDF (eopolicy_pdf) NOT auto-populated (uploaded by staff later).
   *
   * @async
   * @returns {Promise<void>} Updates companyPolicy and saves to database
   *
   * @example
   * // User clicks "USABG" button
   * Store.setPolicyType('company')
   * // → applyCompanyTemplate() called automatically
   * // → companyPolicy fields populated instantly (no latency)
   * // → Form shows: Berkshire Hathaway, N9PL597538, $1M/$3M, dates
   */
  applyCompanyTemplate = async () => {
    if (!this.companyEOTemplate) {
      console.warn('[Eopolicy.store] No company E&O template available, skipping auto-population')
      return
    }

    if (!this.Policies.company) {
      console.warn('[Eopolicy.store] Company policy not found, cannot apply template')
      return
    }

    // Skip if company policy is already populated with matching template data
    const existing = this.Policies.company
    if (
      existing.get('carrier_name') === this.companyEOTemplate.carrier_name &&
      existing.get('eopolicy_num') === this.companyEOTemplate.eopolicy_num &&
      existing.get('eopolicy_pdf')
    ) {
      this.Policy = this.Policies.company
      return
    }

    try {
      const template = this.companyEOTemplate

      // Apply template to company policy fields
      this.Policies.company.set('carrier_name', template.carrier_name)
      this.Policies.company.set('eopolicy_num', template.eopolicy_num)
      this.Policies.company.set('per_coverage_amt', parseFloat(template.per_coverage_amt))
      this.Policies.company.set('agg_coverage_amt', parseFloat(template.agg_coverage_amt))

      // Set dates as YYYY-MM-DD strings for database storage
      // Must use serializeDate() to ensure proper format — raw Date objects
      // serialize as ISO timestamps (e.g. '2025-12-21T05:00:00.000Z') which
      // MySQL DATE columns reject.
      if (template.effective_date) {
        this.Policies.company.set('effective_date', serializeDate(template.effective_date))
      }

      if (template.expiration_date) {
        this.Policies.company.set('expiration_date', serializeDate(template.expiration_date))
      }

      // Save to database
      this.isSaving = true

      try {
        await this.Policies.company.save()

        // After save, convert dates to Date objects for UI date pickers
        runInAction(() => {
          if (template.effective_date) {
            this.Policies.company.set('effective_date', moment(template.effective_date, 'YYYY-MM-DD').toDate())
          }
          if (template.expiration_date) {
            this.Policies.company.set('expiration_date', moment(template.expiration_date, 'YYYY-MM-DD').toDate())
          }
          // Update this.Policy reference to show company policy in form
          this.Policy = this.Policies.company
        })

        // Generate personalized company E&O PDF with agent details
        try {
          const policyId = this.Policies.company.id()
          if (policyId) {
            await UserEopolicyService.generateCompanyPdf(policyId)
            // Re-fetch policy to get fresh model with eopolicy_pdf set
            // (same pattern as onSelectFile — triggers MobX re-render)
            const refreshed = await fetchPolicy(policyId)
            if (refreshed) {
              runInAction(() => {
                this.Policies.company = refreshed
                this.Policy = refreshed
              })
            }
            console.log('[Eopolicy.store] Company E&O PDF generated and uploaded successfully')
          }
        } catch (pdfError) {
          console.error('[Eopolicy.store] Failed to generate company E&O PDF:', pdfError)
        }

        console.log('[Eopolicy.store] Company policy auto-populated and saved successfully')
      } catch (error) {
        console.error('[Eopolicy.store] Failed to save auto-populated company policy:', error)
      } finally {
        runInAction(() => {
          this.isSaving = false
        })
      }
    } catch (error) {
      console.error('[Eopolicy.store] Auto-population failed:', error)
    }
  }
}

export default new EopolicyStore()
